/** @jsx jsx */
import React, { memo, useMemo } from 'react';
import { jsx } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import Spinner from '@atlaskit/spinner';
import { Pressable, xcss, Box } from '@atlaskit/primitives';
import { Text } from '@atlaskit/primitives/compiled';
import type { RichTextCell$key } from '@atlassian/jira-relay/src/__generated__/RichTextCell.graphql';
import type { RichTextCell138BadPattern$key } from '@atlassian/jira-relay/src/__generated__/RichTextCell138BadPattern.graphql';
import { emptyAdfObject } from '@atlassian/jira-rich-content/src/common/adf-parsing-utils.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import {
	CellHoverRenderer,
	HoverPopoverWithContext,
} from '@atlassian/jira-hover-popover/src/ui/popover-with-context/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { CellHoverProvider } from '@atlassian/jira-hover-popover/src/controllers/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	useIsDensityFull,
	useIsSingleLineRowHeightEnabled,
} from '../../../../controllers/features/selectors.tsx';
import { PACKAGE_NAME } from '../../../constants.tsx';
import { RichTextCellCompact } from './rich-text-compact/index.tsx';
import { RichTextCellFull } from './rich-text-full/index.tsx';
import { richTextPopUpContentEntryPoint } from './rich-text-popup-content/entrypoint.tsx';
import { ErrorState } from './error-state/index.tsx';

export type Props = {
	issueKey: string;
	fieldRef: RichTextCell$key;
	/**
	 * Should only be needed For VR testing, to render a storybook example with
	 * the popup content immediately visible
	 */
	shouldOpenOnFirstRender?: boolean;
	issueRef?: RichTextCell138BadPattern$key | null;
	isFirstField?: boolean;
};

export const RichTextCell = memo<Props>(
	({ issueKey, fieldRef, issueRef, shouldOpenOnFirstRender = false, isFirstField }: Props) => {
		const { richText, fieldId } = useFragment<RichTextCell$key>(
			graphql`
				fragment RichTextCell on JiraRichTextField
				@argumentDefinitions(isDensityFull: { type: "Boolean!" }) {
					fieldId
					richText {
						adfValue {
							convertedPlainText @skip(if: $isDensityFull) {
								plainText
							}
							json @include(if: $isDensityFull)
						}
					}
				}
			`,
			fieldRef,
		);

		const issueData = useFragment<RichTextCell138BadPattern$key>(
			graphql`
				fragment RichTextCell138BadPattern on JiraIssue
				@argumentDefinitions(isDensityFull: { type: "Boolean!" }) {
					canBeExported @include(if: $isDensityFull)
				}
			`,
			issueRef || null,
		);

		const isDensityFull = useIsDensityFull();

		const isSingleLineRowHeightEnabled = useIsSingleLineRowHeightEnabled();
		const cloudId = useCloudId();
		const entryPointParams = useMemo(
			() => ({ fieldId, issueKey, cloudId }),
			[fieldId, cloudId, issueKey],
		);
		const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
			richTextPopUpContentEntryPoint,
			entryPointParams,
		);

		if (isSingleLineRowHeightEnabled && !isDensityFull) {
			const previewText = richText?.adfValue?.convertedPlainText?.plainText;

			if (!isFirstField && fg('single_line_row_height_collapsable_fields')) {
				return (
					<Box xcss={readOnlyNoPopoverStyles}>
						<Text maxLines={1}>{previewText}</Text>
					</Box>
				);
			}

			return (
				<CellHoverProvider>
					<CellHoverRenderer>
						{(props) => (
							<HoverPopoverWithContext<HTMLButtonElement>
								content={
									previewText && (
										<JiraEntryPointContainer
											entryPointReferenceSubject={entryPointReferenceSubject}
											id="rich-text-popup-content"
											packageName={PACKAGE_NAME}
											errorFallback={() => <ErrorState />}
											fallback={<Spinner size="medium" />}
											runtimeProps={{}}
										/>
									)
								}
								entryPointActions={entryPointActions}
								isInteractive
								// Applying a fixed width when the content contains media (e.g images) to prevent the popover from collapsing
								// when the popup only contains a single image or an image with a small amount of text
								isFixedWidth={
									fg('issue_table_single_line_row_height_fast_follows')
										? previewText?.includes('[media]')
										: true
								}
								analyticsAttributes={{ cellType: 'rich-text' }}
							>
								{({ ref }) => (
									<Pressable xcss={richTextCellWrapperStyles} ref={ref} {...props}>
										{previewText}
									</Pressable>
								)}
							</HoverPopoverWithContext>
						)}
					</CellHoverRenderer>
				</CellHoverProvider>
			);
		}

		if (isDensityFull) {
			return (
				<RichTextCellFull
					issueKey={issueKey}
					richText={
						fg('endeavour_gadget_empty_rich_text_cell_full_mode')
							? richText?.adfValue?.json || emptyAdfObject
							: richText?.adfValue?.json || ''
					}
					canBeExported={issueData?.canBeExported}
				/>
			);
		}

		return (
			<RichTextCellCompact
				fieldId={fieldId}
				plainText={richText?.adfValue?.convertedPlainText?.plainText || ''}
				issueKey={issueKey}
				shouldOpenOnFirstRender={shouldOpenOnFirstRender}
			/>
		);
	},
);

const richTextCellWrapperStyles = xcss({
	paddingBlock: 'space.050',
	paddingInline: 'space.100',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	cursor: 'pointer',
	display: 'block',
	backgroundColor: 'color.background.neutral.subtle',
	boxSizing: 'border-box',
	height: '100%',
	font: 'font.body',
	width: '100%',
	textAlign: 'left',
	borderWidth: 'border.width.outline',
	borderStyle: 'solid',
	borderRadius: 'radius.small',
	// @ts-expect-error Transparent is not valid but should be
	borderColor: 'transparent',
	':focus': {
		borderColor: 'color.border.focused',
		outline: 'none',
	},
});

const readOnlyNoPopoverStyles = xcss({
	paddingBlock: 'space.050',
	paddingInline: 'space.100',
	display: 'block',
	backgroundColor: 'color.background.neutral.subtle',
	boxSizing: 'border-box',
	height: '100%',
	font: 'font.body',
	width: '100%',
	textAlign: 'left',
	borderWidth: 'border.width.outline',
	borderStyle: 'solid',
	borderRadius: 'radius.small',
	// @ts-expect-error Transparent is not valid but should be
	borderColor: 'transparent',
	':focus': {
		borderColor: 'color.border.focused',
		outline: 'none',
	},
});
